'use client';

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { useZeeloolXUlikeTracking } from './hooks/useZeeloolXUlikeTracking';
import { HeroBanner } from './components/HeroBanner';
import { ProductSection } from './components/ProductSection';
import { UlikeSection } from './components/UlikeSection';
import { CouponSection } from './components/CouponSection';
import SingleImage from './components/SingleImage';
import { ZeeloolXUlikePageData } from './types';
import { ImageTextIntroduction } from './components/ImageTextIntroduction';
import FaqAccordion from '../../components/FaqAccordion';
import StaticFaqForSeo from '../../components/StaticFaqForSeo';

export default function ZeeloolXUlikeClientPage({
  initialData,
  deviceType: serverDeviceType,
}: {
  initialData: ZeeloolXUlikePageData;
  deviceType?: number;
}) {
  const pageData = useMemo(() => initialData, [initialData]);
  const { deviceType: contextDeviceType } = useContext(ResponsiveContext);
  const deviceType = serverDeviceType ?? contextDeviceType;
  const isWeb = deviceType === EPlatform.WEB;

  // App webview 相关状态
  const [inAppH5, setInAppH5] = useState(false);
  const [isUserInfoReady, setIsUserInfoReady] = useState(false);
  const setToken = useStore((state) => state.setToken);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);

  // 使用 ref 跟踪是否已经初始化，避免重复调用
  const hasInitialized = useRef(false);
  const {
    trackPageView,
    trackPageLeave,
    trackProductExp,
    trackProductClick,
    trackADExp,
    trackADClick,
  } = useZeeloolXUlikeTracking();

  // 检测是否在 APP 内
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { userAgent } = navigator;
      const isInApp = userAgent.includes('nextmar-xms-app');
      setInAppH5(isInApp);
      setIsUserInfoReady(!isInApp);
    }
  }, []);

  // App 通信初始化
  useEffect(() => {
    if (inAppH5 && !hasInitialized.current) {
      const handleAppCommunication = async () => {
        try {
          hasInitialized.current = true;

          appCommunicationManager.registerCallback(
            EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
            async (_token) => {
              logger.log('App登录token:', _token);
              if (_token !== 'ClearToken') {
                setToken(_token);
                await refreshAccountInfo();
                setTimeout(() => {
                  setIsUserInfoReady(true);
                }, 1000);
              } else {
                setToken('');
                setUserInfo({});
                setIsUserInfoReady(true);
              }
            },
            {},
            'BackTokenInfo' as EAppCommunicationFuncName,
          );
        } catch (e) {
          logger.error('App通信初始化失败:', e);
          hasInitialized.current = false;
        }
      };

      handleAppCommunication();

      // 添加超时机制，防止 App 通信失败导致页面一直加载
      const timeout = setTimeout(() => {
        if (!isUserInfoReady) {
          logger.warn('App通信超时，将以游客状态继续渲染页面');
          setIsUserInfoReady(true);
        }
      }, 5000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [inAppH5, isUserInfoReady, refreshAccountInfo, setToken, setUserInfo]);

  // 组件卸载时清理 App 通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  useEffect(() => {
    trackPageView();
    return () => {
      trackPageLeave();
    };
  }, [trackPageView, trackPageLeave]);

  return (
    <div className="min-h-screen bg-white">
      {pageData.contents.map((content, index) => {
        switch (content.type) {
          case 'hero_banner':
            return (
              <HeroBanner
                key={`hero-banner-${index}`}
                index={index}
                isWeb={isWeb}
                content={content}
                trackADExp={trackADExp}
              />
            );
          case 'image_text_introduction':
            return (
              <ImageTextIntroduction
                key={`image-text-introduction-${index}`}
                index={index}
                isWeb={isWeb}
                content={content}
                trackADExp={trackADExp}
              />
            );
          case 'single_image':
            return (
              <SingleImage
                key={`single-image-${index}`}
                index={index}
                isWeb={isWeb}
                content={content}
                trackADExp={trackADExp}
              />
            );
          case 'coupon_section':
            return (
              <CouponSection
                key={`coupon-section-${index}`}
                index={index}
                isWeb={isWeb}
                content={content}
                trackADExp={trackADExp}
                trackADClick={trackADClick}
              />
            );
          case 'ulike_section':
            return (
              <UlikeSection
                key={`ulike-section-${index}`}
                index={index}
                isWeb={isWeb}
                content={content}
                trackADExp={trackADExp}
                trackADClick={trackADClick}
              />
            );
          case 'product_section':
            return (
              <ProductSection
                key={`product-section-${index}`}
                isWeb={isWeb}
                content={content}
                trackProductExp={trackProductExp}
                trackProductClick={trackProductClick}
              />
            );
          default:
            return null;
        }
      })}
      {pageData.seo_info?.faq?.length > 0 && (
        <FaqAccordion
          className="bg-[rgba(70,58,232,.1)]"
          faqData={{
            title: pageData.seo_info.h1 || '',
            faq_arr: pageData.seo_info.faq.map((item) => ({
              question: item.question,
              answer: item.answer,
            })),
          }}
        />
      )}
      {pageData.seo_info?.h1 && (
        <h1 className="w-full fixed bottom-0 left-0 -translate-x-[100%] ">
          {pageData.seo_info?.h1}
        </h1>
      )}
      {pageData.seo_info?.faq?.length > 0 && (
        <StaticFaqForSeo
          faqData={{
            title: pageData.seo_info.h1 || '',
            faq_arr: pageData.seo_info.faq,
          }}
        />
      )}
    </div>
  );
}
